import { memo } from 'react';
import PropTypes from 'prop-types';

const PADDING_STYLES = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

/** @type {Record<string, string>} */
const VARIANT_STYLES = {
  default: 'border-edge bg-surface dark:bg-gray-800/80',
  muted: 'border-edge bg-surface-muted/80 dark:bg-gray-900/50',
  purple: 'border-purple-500/25 bg-purple-500/5 dark:bg-purple-950/30',
  blue: 'border-blue-500/25 bg-blue-500/5 dark:bg-blue-950/25',
  green: 'border-emerald-500/25 bg-emerald-500/5 dark:bg-emerald-950/25',
  amber: 'border-amber-500/25 bg-amber-500/5 dark:bg-amber-950/25',
  red: 'border-red-500/25 bg-red-500/5 dark:bg-red-950/25',
};

/**
 * Base card surface for drawer body content: rounded container with subtle border
 * and optional color tint.
 *
 * @param {import('react').ReactNode} props.children
 * @param {'default'|'muted'|'purple'|'blue'|'green'|'amber'|'red'} [props.variant='default']
 * @param {'none'|'sm'|'md'|'lg'} [props.padding='md']
 * @param {boolean} [props.interactive=false]  Enables hover/focus affordance when `onClick` is provided.
 * @param {string} [props.className]
 */
const DrawerPanel = ({
  children,
  variant = 'default',
  padding = 'md',
  interactive = false,
  className = '',
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...rest
}) => {
  const v = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;
  const p = PADDING_STYLES[padding] ?? PADDING_STYLES.md;
  const isInteractive = interactive && typeof onClick === 'function';

  const handleKeyDown = isInteractive
    ? (event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(event);
        }
      }
    : onKeyDown;

  return (
    <div
      className={[
        'rounded-xl border',
        v,
        p,
        isInteractive
          ? 'cursor-pointer transition-colors hover:bg-surface-muted/60 dark:hover:bg-gray-800/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface'
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? (role ?? 'button') : role}
      tabIndex={isInteractive ? (tabIndex ?? 0) : tabIndex}
      {...rest}
    >
      {children}
    </div>
  );
};

DrawerPanel.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'muted', 'purple', 'blue', 'green', 'amber', 'red']),
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  interactive: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  role: PropTypes.string,
  tabIndex: PropTypes.number,
};

DrawerPanel.displayName = 'DrawerPanel';

export default memo(DrawerPanel);
